/**
 * 許可權管理工具函式
 */

import permissionData from './permission.json'

// 角色定義
export const ROLES = {
  OWNER: 'owner',
  MANAGER: 'manager',
  DESIGNER: 'designer',
  RECEPTIONIST: 'receptionist',
  ACCOUNTANT: 'accountant',
  EMPLOYEE: 'employee'
}

// 角色中文名稱對映
export const ROLE_NAMES = {
  owner: '老闆/擁有者',
  manager: '店長',
  designer: '設計師',
  receptionist: '櫃檯人員',
  accountant: '會計',
  employee: '一般員工'
}

// 角色描述
export const ROLE_DESCRIPTIONS = {
  owner: '擁有所有許可權，可管理整個商家',
  manager: '高階管理許可權，可管理員工、預約、財務等',
  designer: '可管理自己的預約和服務',
  receptionist: '可管理所有預約和客戶資料',
  accountant: '可檢視財務報表和營收資料',
  employee: '基礎許可權，僅能檢視自己的資料'
}

// 角色顏色（用於UI顯示）
export const ROLE_COLORS = {
  owner: '#f5222d',
  manager: '#fa8c16',
  designer: '#52c41a',
  receptionist: '#1890ff',
  accountant: '#722ed1',
  employee: '#8c8c8c'
}

// 帳號狀態
export const ACCOUNT_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended'
}

// 帳號狀態中文名稱
export const ACCOUNT_STATUS_NAMES = {
  active: '啟用',
  inactive: '停用',
  suspended: '暫停'
}

// 許可權模組（從 permission.json 動態生成）
export const PERMISSION_MODULES = Object.keys(
  permissionData.permissions
).reduce((acc, key) => {
  acc[key.toUpperCase()] = key
  return acc
}, {})

// 從 permission.json 動態生成許可權映射
const generatePermissionsMap = () => {
  const permissionsMap = {}

  // 處理所有模塊的權限
  Object.values(permissionData.permissions).forEach((module) => {
    module.permissions.forEach((perm) => {
      permissionsMap[perm.key] = perm.label
    })
  })

  // 處理特殊權限
  permissionData.specialPermissions.forEach((perm) => {
    permissionsMap[perm.key] = perm.label
  })

  return permissionsMap
}

// 許可權點定義（用於UI展示）- 從 permission.json 動態生成
export const PERMISSIONS_MAP = generatePermissionsMap()

// 導出角色默認權限配置（用於後端同步）
export const DEFAULT_ROLES = permissionData.defaultRoles

// 導出權限模塊信息（用於UI分組顯示）
export const PERMISSION_MODULES_DATA = permissionData.permissions

/**
 * 檢查使用者是否擁有指定許可權
 * @param {Array<string>} userPermissions - 使用者的許可權列表
 * @param {string} requiredPermission - 需要的許可權
 * @returns {boolean}
 */
export const hasPermission = (userPermissions, requiredPermission) => {
  if (!userPermissions || userPermissions.length === 0) {
    return false
  }

  // 1. 檢查全域性許可權
  if (userPermissions.includes('*')) {
    return true
  }

  // 2. 檢查精確匹配
  if (userPermissions.includes(requiredPermission)) {
    return true
  }

  // 3. 檢查模組萬用字元（如 booking:*）
  const [module] = requiredPermission.split(':')
  if (userPermissions.includes(`${module}:*`)) {
    return true
  }

  return false
}

/**
 * 檢查是否擁有多個許可權之一（OR 邏輯）
 * @param {Array<string>} userPermissions - 使用者的許可權列表
 * @param {Array<string>} requiredPermissions - 需要的許可權列表
 * @returns {boolean}
 */
export const hasAnyPermission = (userPermissions, requiredPermissions) => {
  return requiredPermissions.some((perm) =>
    hasPermission(userPermissions, perm)
  )
}

/**
 * 檢查是否擁有所有許可權（AND 邏輯）
 * @param {Array<string>} userPermissions - 使用者的許可權列表
 * @param {Array<string>} requiredPermissions - 需要的許可權列表
 * @returns {boolean}
 */
export const hasAllPermissions = (userPermissions, requiredPermissions) => {
  return requiredPermissions.every((perm) =>
    hasPermission(userPermissions, perm)
  )
}

/**
 * 檢查是否為商家擁有者
 * @param {string} businessRole - 使用者的角色
 * @returns {boolean}
 */
export const isOwner = (businessRole) => {
  return businessRole === ROLES.OWNER
}

/**
 * 檢查是否可以修改目標角色
 * @param {string} currentRole - 當前使用者角色
 * @param {string} targetRole - 目標角色
 * @returns {boolean}
 */
export const canModifyRole = (currentRole, targetRole) => {
  // owner 不能被修改
  if (targetRole === ROLES.OWNER) {
    return false
  }

  // 只有 owner 可以設定 manager
  if (targetRole === ROLES.MANAGER && currentRole !== ROLES.OWNER) {
    return false
  }

  // owner 和 manager 可以修改其他角色
  return currentRole === ROLES.OWNER || currentRole === ROLES.MANAGER
}

/**
 * 獲取角色權重（用於比較許可權級別）
 * @param {string} role - 角色
 * @returns {number}
 */
export const getRoleWeight = (role) => {
  const weights = {
    owner: 100,
    manager: 80,
    accountant: 60,
    receptionist: 50,
    designer: 40,
    employee: 20
  }
  return weights[role] || 0
}

/**
 * 格式化操作日誌的action
 * @param {string} action - 操作型別
 * @returns {string}
 */
export const formatActionText = (action) => {
  const actionMap = {
    'employee:create': '建立員工',
    'employee:update': '更新員工',
    'employee:delete': '刪除員工',
    'employee:update_role': '更新角色',
    'employee:update_status': '更新狀態',
    'employee:update_schedule': '更新排班',
    'booking:create': '建立預約',
    'booking:update': '更新預約',
    'booking:cancel': '取消預約',
    'booking:delete': '刪除預約',
    'business:edit': '編輯商家',
    'business:edit_service': '編輯服務',
    'customer:create': '建立客戶',
    'customer:update': '更新客戶',
    'customer:delete': '刪除客戶'
  }
  return actionMap[action] || action
}

/**
 * 按模組分組許可權
 * @param {Array<string>} permissions - 許可權列表
 * @returns {Object}
 */
export const groupPermissionsByModule = (permissions) => {
  const grouped = {}

  permissions.forEach((perm) => {
    const [module] = perm.split(':')
    if (!grouped[module]) {
      grouped[module] = []
    }
    grouped[module].push(perm)
  })

  return grouped
}
